import type { Widgets } from '@common/enums';

const WIDGET_ENTITY_TYPE_PREFIX = 'widget:';

/** A versioned widget's `entityType`: `widget:<kind>` (`widget:line`). */
export type WidgetEntityType<K extends string = string> = `widget:${K}`;

/**
 * A versioned widget envelope: `{ id, parentId, entityType: 'widget:<kind>',
 * entitySubType: 'default' | 'zero', version, meta, props }`. Only the fields the
 * helpers below read.
 */
export type WidgetEnvelopeLike = {
    id: string;
    parentId?: string;
    entityType: WidgetEntityType;
    entitySubType: string;
    version: number;
    meta?: { displayId?: string | null };
};

/** Legacy and versioned widgets side by side, typed only as far as these helpers need. */
type AnyWidgetLike = { id?: string; name?: string; displayId?: string | null } | WidgetEnvelopeLike;

/** Whether an `entityType` names a widget — grids, tabs and blocks carry their own. */
export function isWidgetEntityType(entityType: unknown): entityType is WidgetEntityType {
    return typeof entityType === 'string' && entityType.startsWith(WIDGET_ENTITY_TYPE_PREFIX);
}

/**
 * A pre-versioning widget — it lacks the `entityType: 'widget:<kind>'` + numeric
 * `version` pair every versioned widget carries.
 */
export function isLegacyWidget<T>(entity: T): entity is Exclude<T, WidgetEnvelopeLike> {
    if (typeof entity !== 'object' || entity === null) {
        return true;
    }

    const { entityType, version } = entity as { entityType?: unknown; version?: unknown };

    return !(isWidgetEntityType(entityType) && typeof version === 'number');
}

/** The widget kind: the legacy `name`, or the `<kind>` of the versioned `widget:<kind>`. */
export function widgetNameOf(entity: AnyWidgetLike | null | undefined): Widgets | undefined {
    if (!entity) {
        return undefined;
    }

    return (
        isLegacyWidget(entity)
            ? (entity as { name?: string }).name
            : (entity as WidgetEnvelopeLike).entityType.slice(WIDGET_ENTITY_TYPE_PREFIX.length)
    ) as Widgets | undefined;
}

/**
 * The id of the column-level container a widget hangs off, as the column hosts
 * (`default-widget`, specific sections) use it: the legacy `columnId`, or the
 * versioned `parentId` — which for a widget in a grid cell is the grid.
 */
export function widgetParentIdOf(entity: AnyWidgetLike | null | undefined): string | undefined {
    if (!entity) {
        return undefined;
    }

    return isLegacyWidget(entity)
        ? (entity as { columnId?: string }).columnId
        : (entity as WidgetEnvelopeLike).parentId;
}

/**
 * Whether a widget's tablet / mobile settings follow desktop: legacy `data.isSynchronizeStyle`,
 * versioned `props.syncWithDesktop`. A widget without the flag has nothing per device to diverge.
 */
export function isWidgetSynchronized(entity: AnyWidgetLike | null | undefined): boolean {
    if (!entity) {
        return true;
    }

    const [settings, flag] = isLegacyWidget(entity)
        ? [(entity as { data?: unknown }).data, 'isSynchronizeStyle']
        : [(entity as { props?: unknown }).props, 'syncWithDesktop'];

    if (typeof settings === 'object' && settings !== null && flag in settings) {
        return Boolean((settings as Record<string, unknown>)[flag]);
    }

    return true;
}

/** The element id a widget renders with: the user-set display id (legacy root, versioned `meta`), else `widget-${id}`. */
export function widgetDisplayIdOf(entity: AnyWidgetLike): string {
    const displayId = isLegacyWidget(entity)
        ? (entity as { displayId?: string | null }).displayId
        : (entity as WidgetEnvelopeLike).meta?.displayId;

    return displayId || `widget-${entity.id}`;
}
